import { useCallback, useState } from 'react';

/**
 * Unified playback state for any video surface. Source-agnostic: callers
 * drive state via event callbacks (`onTimeChange`, `onPlay`, `onPause`,
 * `onDurationChange`). The hook owns `seekTime` clearing logic so every
 * consumer (renderer, MuxPlayer, future sources) gets consistent behavior.
 *
 * `seekTime` is the local drag-time override: non-null while any scrubber
 * is being dragged. Consumers paint `seekTime ?? currentTime` so every
 * scrubber on the same surface stays visually in sync during a drag.
 */
export interface PlaybackState {
  isPlaying: boolean;
  currentTime: number;
  duration: number;
  seekTime: number | null;
  isSeeking: boolean;
}

export interface UsePlaybackStateOptions {
  autoPlay?: boolean;
}

export interface UsePlaybackState {
  playbackState: PlaybackState;
  /** Call when the source reports a new current time. */
  onTimeChange: (time: number) => void;
  /** Call when the source starts playing. */
  onPlay: () => void;
  /** Call when the source pauses. */
  onPause: () => void;
  /** Call when the source's duration changes. */
  onDurationChange: (duration: number) => void;
  /** Drag-time override setter. Set on drag start / move; clear (`null`) on release. */
  setSeekTime: (time: number | null) => void;
  /** Drag flag setter. True on drag start, false on release. */
  setIsSeeking: (value: boolean) => void;
}

export function usePlaybackState(
  options?: UsePlaybackStateOptions,
): UsePlaybackState {
  const { autoPlay = false } = options ?? {};

  const [playbackState, setPlaybackState] = useState<PlaybackState>({
    isPlaying: autoPlay,
    currentTime: 0,
    duration: 0,
    seekTime: null,
    isSeeking: false,
  });

  const onTimeChange = useCallback((time: number) => {
    setPlaybackState(prev => {
      if (prev.currentTime === time) return prev;
      const shouldClearSeek =
        prev.seekTime !== null &&
        !prev.isSeeking &&
        Math.abs(time - prev.seekTime) < 0.5;
      return {
        ...prev,
        currentTime: time,
        seekTime: shouldClearSeek ? null : prev.seekTime,
        isSeeking: shouldClearSeek ? false : prev.isSeeking,
      };
    });
  }, []);

  const onPlay = useCallback(() => {
    setPlaybackState(prev =>
      prev.isPlaying ? prev : { ...prev, isPlaying: true },
    );
  }, []);

  const onPause = useCallback(() => {
    setPlaybackState(prev =>
      !prev.isPlaying ? prev : { ...prev, isPlaying: false },
    );
  }, []);

  const onDurationChange = useCallback((duration: number) => {
    setPlaybackState(prev =>
      prev.duration === duration ? prev : { ...prev, duration },
    );
  }, []);

  const setSeekTime = useCallback((time: number | null) => {
    setPlaybackState(prev =>
      prev.seekTime === time ? prev : { ...prev, seekTime: time },
    );
  }, []);

  const setIsSeeking = useCallback((value: boolean) => {
    setPlaybackState(prev =>
      prev.isSeeking === value ? prev : { ...prev, isSeeking: value },
    );
  }, []);

  return {
    playbackState,
    onTimeChange,
    onPlay,
    onPause,
    onDurationChange,
    setSeekTime,
    setIsSeeking,
  };
}
